import { jwtDecode } from 'jwt-decode';

import { AUTH_STORAGE_KEYS } from '../constants/auth.constants';
import { UserProfile, TokenData, DecodedToken } from '../types/auth.types';

/**
 * Checks if User is Authenticated based on presence and expiration of access token
 */
export const isAuthenticated = (): boolean => {
  const token = localStorage.getItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN);
  if (!token) return false;

  const expiresAt = localStorage.getItem(AUTH_STORAGE_KEYS.EXPIRES_AT);
  if (expiresAt && new Date().getTime() > parseInt(expiresAt, 10)) {
    clearSession();
    return false;
  }
  return true;
};

/**
 * Gets the access token from storage
 */
export const getAccessToken = (): string | null => {
  return localStorage.getItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN) || sessionStorage.getItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN);
};

/**
 * Gets User Info from Local Storage or returns null
 */
export const getUserInfo = async (): Promise<UserProfile | null> => {
  if (!isAuthenticated()) return null;

  // Return stored info if available
  const storedInfo = localStorage.getItem(AUTH_STORAGE_KEYS.USER_INFO);
  if (storedInfo) {
    try {
      return JSON.parse(storedInfo) as UserProfile;
    } catch {
      localStorage.removeItem(AUTH_STORAGE_KEYS.USER_INFO);
    }
  }

  return null;
};

/**
 * Gets User Info synchronously from Local Storage or returns null
 */
export const getUserInfoSync = (): UserProfile | null => {
  const storedInfo = localStorage.getItem(AUTH_STORAGE_KEYS.USER_INFO);
  if (storedInfo) {
    try {
      return JSON.parse(storedInfo) as UserProfile;
    } catch {
      localStorage.removeItem(AUTH_STORAGE_KEYS.USER_INFO);
    }
  }
  return null;
};

/**
 * Stores session data in local storage
 */
export const _storeSession = (tokenData: TokenData): void => {
  localStorage.setItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN, tokenData.access_token);
  localStorage.setItem(AUTH_STORAGE_KEYS.TOKEN_TYPE, tokenData.token_type);
  localStorage.setItem(AUTH_STORAGE_KEYS.SCOPE, tokenData.scope);
  localStorage.setItem(AUTH_STORAGE_KEYS.EXPIRES_IN, tokenData.expires_in.toString());

  const expiresAt = new Date().getTime() + (tokenData.expires_in * 1000);
  localStorage.setItem(AUTH_STORAGE_KEYS.EXPIRES_AT, expiresAt.toString());

  try {
    const decodedToken = jwtDecode<DecodedToken>(tokenData.access_token);
    const userInfo: UserProfile = {
      email: decodedToken.email,
      given_name: decodedToken.given_name,
      family_name: decodedToken.family_name,
      name: decodedToken.name || `${decodedToken.given_name} ${decodedToken.family_name}`,
      exp: decodedToken.exp,
      iat: decodedToken.iat,
      user_profile_id: decodedToken.sub,
      sub: decodedToken.sub
    };

    localStorage.setItem(AUTH_STORAGE_KEYS.USER_INFO, JSON.stringify(userInfo));
    localStorage.setItem(AUTH_STORAGE_KEYS.EMAIL, userInfo.email);
    // UI specific display items
    localStorage.setItem(AUTH_STORAGE_KEYS.GIVEN_NAME, userInfo.given_name);
    localStorage.setItem(AUTH_STORAGE_KEYS.FAMILY_NAME, userInfo.family_name);

    // Mutate tokenData to include userInfo for immediate return if needed
    tokenData.userInfo = userInfo;
  } catch (_e) {
    // Ignore error
  }
};

/**
 * Clears all auth and cart session data from storage
 */
export const clearSession = (): void => {
  // Clear Ping authentication data from local storage
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith('ping_')) {
      localStorage.removeItem(key);
      i--;
    }
  }

  // We intentionally DO NOT remove ping_code_verifier here.
  // If a token expires during an app boot (redirect back from ping), clearSession() runs.
  // Preserving the verifier allows handleCallback() to still successfully authenticate.

  // Clear cart data on logout to prevent cart persistence across user sessions
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith(AUTH_STORAGE_KEYS.CART_ID)) {
      localStorage.removeItem(key);
      i--;
    }
  }
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key && key.startsWith(AUTH_STORAGE_KEYS.CART_ID)) {
      sessionStorage.removeItem(key);
      i--;
    }
  }
  sessionStorage.removeItem(AUTH_STORAGE_KEYS.CART_RESOLVED);

  // CRITICAL: Clear anonymous cart ID cookie to prevent the same cart from being
  // found twice (once by customerEmail, once by anonymousId) during login merge,
  // which causes cart item quantities to double
  document.cookie = 'anonymousCartId=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=Lax';

  // Clear any post-login redirects
  localStorage.removeItem(AUTH_STORAGE_KEYS.POST_LOGIN_REDIRECT);
};
